// PublicDemoPage — outcome-led, neutral labels, reuses Trajectory3D so the
// same viewer that powers the operator console powers the public gallery.
// Public demo cards display "Engagement 0X" only — geometry names never
// leak to the public surface.

import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Play } from "lucide-react";
import { Link } from "react-router-dom";

import { publicSite } from "@/api/endpoints";
import Trajectory3D from "@/components/Trajectory3D";
import SeoHead from "@/components/SeoHead";
import { buildBreadcrumbListSchema } from "@/lib/schema";

function fmt(value, decimals = 2, suffix = "") {
    if (value == null || Number.isNaN(value)) return "—";
    return Number(value).toFixed(decimals) + suffix;
}

// Map a backend identifier (e.g. "demo_03_high_altitude_crossing") to the
// neutral public label. Internal identifiers and the authenticated
// console keep the descriptive form — only the marketing surface is
// reframed.
function neutralLabel(demo_name) {
    const m = (demo_name || "").match(/^demo_0?(\d+)/i);
    const n = m ? parseInt(m[1], 10) : 0;
    return n > 0 ? `Engagement ${String(n).padStart(2, "0")}` : "Engagement";
}

// One short, quantified subtitle per card. No category names, no
// implementation hints — only the outcome the operator can quote.
function outcomeSubtitle(d) {
    const miss = d.miss_distance_m;
    const t = d.intercept_time_s;
    const hasMiss = Number.isFinite(miss);
    const hasT = Number.isFinite(t);
    if (hasMiss && hasT && miss < 2.0) {
        return `Verified hit · ${miss.toFixed(2)} m miss · ${t.toFixed(1)} s to intercept`;
    }
    if (hasMiss && hasT) return `${miss.toFixed(2)} m miss · ${t.toFixed(1)} s to intercept`;
    if (hasMiss) return `${miss.toFixed(2)} m miss`;
    return "Reference run";
}

export default function PublicDemoPage() {
    const [demos, setDemos] = useState([]);
    const [active, setActive] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        publicSite
            .demos()
            .then((res) => {
                setDemos(res.demos || []);
                setLoading(false);
            })
            .catch((e) => {
                setErr(e.message || "Could not load demonstrations.");
                setLoading(false);
            });
    }, []);

    // Stable order: by extracted engagement number.
    const ordered = useMemo(() => {
        const list = [...demos];
        list.sort((a, b) => {
            const an = parseInt((a.demo_name || "").match(/^demo_0?(\d+)/i)?.[1] || "0", 10);
            const bn = parseInt((b.demo_name || "").match(/^demo_0?(\d+)/i)?.[1] || "0", 10);
            return an - bn;
        });
        return list;
    }, [demos]);

    // Bind the public fetcher once — same shape as the authenticated data source.
    const publicFetcher = (id, opts) =>
        publicSite.demoTrajectory(id, opts?.max_samples ?? 12, "auto");

    if (active) {
        const label = neutralLabel(active.demo_name);
        return (
            <div data-testid="public-demo-detail">
                <section className="section" style={{ paddingTop: 48, paddingBottom: 32 }}>
                    <div className="section__inner">
                        <button
                            type="button"
                            onClick={() => setActive(null)}
                            className="btn-secondary"
                            data-testid="public-demo-back"
                            style={{ marginBottom: 24 }}
                        >
                            <ArrowLeft size={14} /> Back to gallery
                        </button>
                        <span className="section__eyebrow">// {label}</span>
                        <h1 className="section__heading">{label}</h1>
                        <div className="banner" style={{ marginTop: 12, marginBottom: 24 }}>
                            Miss distance{" "}
                            <strong>{fmt(active.miss_distance_m, 2, " m")}</strong>
                            {" · "}
                            Time to intercept{" "}
                            <strong>{fmt(active.intercept_time_s, 2, " s")}</strong>
                            {" · "}
                            Peak load <strong>{fmt(active.max_g, 2, " g")}</strong>
                        </div>
                        <Trajectory3D
                            runId={active.run_id}
                            status="succeeded"
                            hasTrajectoryArtifact={true}
                            fetcher={publicFetcher}
                        />
                    </div>
                </section>
            </div>
        );
    }

    return (
        <>
            <SeoHead
                path="/demo"
                schemaJsonLd={[
                    buildBreadcrumbListSchema({ path: "/demo", label: "Demo" }),
                ]}
            />
        <div data-testid="public-demo-page">
            <section className="section" style={{ paddingTop: 64 }}>
                <div className="section__inner">
                    <span className="section__eyebrow">// VERIFIED INTERCEPTS</span>
                    <h1 className="section__heading">
                        Six engagements. Real runs. Quantified outcomes.
                    </h1>
                    <p className="section__lede">
                        Every UAVSimNet deployment ships with a curated set of verified
                        intercepts you can open, rotate, and inspect right now. These
                        are not renders. Each card below is a real run on the same
                        platform your team will use — the miss distance, the time to
                        intercept, the trajectory geometry are all measured outputs.
                    </p>
                    {err && (
                        <div
                            className="banner banner--danger"
                            style={{ marginTop: 24 }}
                            data-testid="public-demo-error"
                        >
                            {err}
                        </div>
                    )}
                    {loading ? (
                        <div
                            className="banner"
                            style={{ marginTop: 32 }}
                            data-testid="public-demos-loading"
                        >
                            Loading demonstrations…
                        </div>
                    ) : (
                        <div className="demo-grid" data-testid="public-demo-grid">
                            {ordered.map((d) => {
                                const label = neutralLabel(d.demo_name);
                                return (
                                    <button
                                        type="button"
                                        key={d.run_id}
                                        className="demo-tile"
                                        onClick={() => setActive(d)}
                                        data-testid={`public-demo-tile-${label.replace(/\s+/g, "-").toLowerCase()}`}
                                    >
                                        <div className="demo-tile__name">{label}</div>
                                        <div className="demo-tile__category">
                                            {outcomeSubtitle(d)}
                                        </div>
                                        <div className="demo-tile__metrics">
                                            <div>
                                                <div className="demo-metric__label">
                                                    Miss distance
                                                </div>
                                                <div className="demo-metric__value">
                                                    {fmt(d.miss_distance_m, 2, " m")}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="demo-metric__label">
                                                    Time to intercept
                                                </div>
                                                <div className="demo-metric__value">
                                                    {fmt(d.intercept_time_s, 2, " s")}
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            style={{
                                                marginTop: 16,
                                                color: "var(--ux-accent)",
                                                fontSize: 13,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 6,
                                            }}
                                        >
                                            <Play size={14} /> View trajectory
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    <p
                        style={{
                            color: "var(--ux-text-3)",
                            fontSize: 13,
                            marginTop: 28,
                            fontStyle: "italic",
                        }}
                        data-testid="public-demo-readonly-note"
                    >
                        Demonstrations are read-only. Customers with active access build
                        their own scenarios in the operator console.
                    </p>
                </div>
            </section>
            <section className="section section--alt">
                <div className="section__inner" style={{ textAlign: "center" }}>
                    <h2 className="section__heading">
                        Run your own threats. Defend your own choices.
                    </h2>
                    <p className="section__lede" style={{ margin: "0 auto 24px" }}>
                        Customers get the same console, the same physics, and the same
                        outputs against their own threat library — the day onboarding
                        completes.
                    </p>
                    <Link
                        to="/contact"
                        className="btn-primary"
                        data-testid="public-demo-cta"
                    >
                        Request access <ArrowRight size={16} />
                    </Link>
                    <Link
                        to="/features"
                        className="btn-ghost"
                        data-testid="public-demo-cta-features"
                    >
                        Counter-UAS simulation features
                    </Link>
                    <Link
                        to="/evidence"
                        className="btn-ghost"
                        data-testid="public-demo-cta-evidence"
                    >
                        Evidence-grade credibility framework
                    </Link>
                    <Link
                        to="/use-cases"
                        className="btn-ghost"
                        data-testid="public-demo-cta-use-cases"
                    >
                        Use cases
                    </Link>
                </div>
            </section>
        </div>
        </>
    );
}
